import type { HTTP } from "@/core/http";
import type {
  CreateRfpAnalysisDetailInput,
  GenerateRfpAnalysisParams,
  GenerateRfpAnalysisResult,
  RfpAnalysisDetail,
  RfpAnalysisDetailApiResponse,
  RfpAnalysisDetailHistory,
  RfpAnalysisMetadata,
  RfpAnalysisMetadataApiResponse,
  RfpAnalysisSummary,
  RfpAnalysisSummaryApiResponse,
  RfpAnalysisSummaryHistory,
  RfpGenerationApiResponse,
  RfpGenerationPmsSave,
  UpdateRfpAnalysisDetailInput,
  UpdateRfpAnalysisSummaryInput,
} from "../types";
import {
  RFP_ANALYSIS_STATUS_DRAFT,
  listRfpAnalysisModules,
} from "../utils/rfpAnalysisMetadataDisplay";

const BASE = "/pms/rfp-analysis";

function isNotFoundError(error: unknown): boolean {
  return error instanceof Error && /not found/i.test(error.message);
}

/** PMS returns `{}` (HTTP 200) when the project has no summary yet. */
function hasSummaryPayload(
  raw: Partial<RfpAnalysisSummaryApiResponse> | null | undefined,
): raw is RfpAnalysisSummaryApiResponse {
  return raw != null && raw.rfpAnalStatus != null;
}

/** HTTP client for RFP Analysis (`/pms/rfp-analysis/...`). */
export class RfpAnalysisService {
  constructor(private readonly httpClient: HTTP) {}

  private fromApiSummary(raw: RfpAnalysisSummaryApiResponse): RfpAnalysisSummary {
    return {
      projectId: raw.projectId,
      clientName: raw.rfpAnalClientName,
      clientContact: raw.rfpAnalClientContact,
      rfpReference: raw.rfpAnalRfpReference,
      expectedProjectDuration: raw.rfpAnalExpectedProjectDuration,
      objectives: raw.rfpAnalObjectives,
      milestones: raw.rfpAnalMilestones,
      industryDomain: raw.rfpAnalIndustryDomain,
      techStack: raw.rfpAnalTechStack,
      inScope: raw.rfpAnalInScope,
      outOfScope: raw.rfpAnalOutOfScope,
      functionalRequirements: raw.rfpAnalFunctionalRequirements,
      nonFunctionalRequirements: raw.rfpAnalNonFunctionalRequirements,
      constraints: raw.rfpAnalConstraints,
      dependencies: raw.rfpAnalDependencies,
      compliance: raw.rfpAnalCompliance,
      deliverables: raw.rfpAnalDeliverables,
      assumptions: raw.rfpAnalAssumptions,
      externalInterfaces: raw.rfpAnalExternalInterfaces,
      keyStakeholders: raw.rfpAnalKeyStakeholders,
      successCriteria: raw.rfpAnalSuccessCriteria,
      meetingNotes: raw.rfpAnalMeetingNotes,
      source: raw.rfpAnalSource,
      status: raw.rfpAnalStatus,
      remarks: raw.rfpAnalRemarks,
      updatedBy: raw.rfpAnalUpdatedBy,
      updatedAt: raw.rfpAnalUpdatedAt,
    };
  }

  private fromApiDetail(raw: RfpAnalysisDetailApiResponse): RfpAnalysisDetail {
    return {
      id: raw.rfpAnalDetId,
      projectId: raw.projectId,
      section: raw.rfpAnalDetSection,
      module: raw.rfpAnalDetModule,
      title: raw.rfpAnalDetTitle,
      description: raw.rfpAnalDetDescription,
      originDocType: raw.rfpAnalDetOriginDocType,
      originDocName: raw.rfpAnalDetOriginDocName,
      originLocation: raw.rfpAnalDetOriginLocation,
      sequence: raw.rfpAnalDetSequence,
      source: raw.rfpAnalDetSource,
      status: raw.rfpAnalDetStatus,
      remarks: raw.rfpAnalDetRemarks,
      updatedBy: raw.rfpAnalDetUpdatedBy,
      updatedAt: raw.rfpAnalDetUpdatedAt,
    };
  }

  private toSummaryUpdatePayload(data: UpdateRfpAnalysisSummaryInput) {
    return {
      rfpAnalClientName: data.clientName ?? "",
      rfpAnalClientContact: data.clientContact ?? "",
      rfpAnalRfpReference: data.rfpReference ?? "",
      rfpAnalExpectedProjectDuration: data.expectedProjectDuration ?? "",
      rfpAnalObjectives: data.objectives ?? "",
      rfpAnalMilestones: data.milestones ?? "",
      rfpAnalIndustryDomain: data.industryDomain ?? "",
      rfpAnalTechStack: data.techStack ?? "",
      rfpAnalInScope: data.inScope ?? "",
      rfpAnalOutOfScope: data.outOfScope ?? "",
      rfpAnalFunctionalRequirements: data.functionalRequirements ?? "",
      rfpAnalNonFunctionalRequirements: data.nonFunctionalRequirements ?? "",
      rfpAnalConstraints: data.constraints ?? "",
      rfpAnalDependencies: data.dependencies ?? "",
      rfpAnalCompliance: data.compliance ?? "",
      rfpAnalDeliverables: data.deliverables ?? "",
      rfpAnalAssumptions: data.assumptions ?? "",
      rfpAnalExternalInterfaces: data.externalInterfaces ?? "",
      rfpAnalKeyStakeholders: data.keyStakeholders ?? "",
      rfpAnalSuccessCriteria: data.successCriteria ?? "",
      rfpAnalMeetingNotes: data.meetingNotes ?? "",
      rfpAnalStatus: data.status,
      rfpAnalRemarks: data.remarks,
    };
  }

  private toDetailPayload(
    data: CreateRfpAnalysisDetailInput | UpdateRfpAnalysisDetailInput,
  ) {
    return {
      projectId: data.projectId,
      rfpAnalDetSection: data.section,
      rfpAnalDetModule: data.module,
      rfpAnalDetTitle: data.title,
      rfpAnalDetDescription: data.description,
      rfpAnalDetOriginDocType: data.originDocType,
      rfpAnalDetOriginDocName: data.originDocName,
      rfpAnalDetOriginLocation: data.originLocation,
      rfpAnalDetStatus: data.status ?? RFP_ANALYSIS_STATUS_DRAFT,
      rfpAnalDetRemarks: data.remarks,
    };
  }

  async getMetadata(): Promise<RfpAnalysisMetadata> {
    const raw = await this.httpClient.get<RfpAnalysisMetadataApiResponse>(
      `${BASE}/metadata`,
    );
    return {
      section: raw.section,
      source: raw.source,
      status: raw.status,
      originDocType: raw.origin_doc_type,
    };
  }

  /** Returns null when the project has no summary yet (empty body or 404). */
  async getSummary(projectId: number): Promise<RfpAnalysisSummary | null> {
    try {
      const raw = await this.httpClient.get<
        Partial<RfpAnalysisSummaryApiResponse>
      >(`${BASE}/project/${projectId}`);
      if (!hasSummaryPayload(raw)) return null;
      return this.fromApiSummary(raw);
    } catch (error) {
      if (isNotFoundError(error)) return null;
      throw error;
    }
  }

  async getSummaryHistory(
    projectId: number,
  ): Promise<RfpAnalysisSummaryHistory[]> {
    return this.httpClient.get<RfpAnalysisSummaryHistory[]>(
      `${BASE}/project/${projectId}/history`,
    );
  }

  async updateSummary(
    projectId: number,
    data: UpdateRfpAnalysisSummaryInput,
  ): Promise<RfpAnalysisSummary> {
    const raw = await this.httpClient.put<RfpAnalysisSummaryApiResponse>(
      `${BASE}/project/${projectId}`,
      { body: this.toSummaryUpdatePayload(data) },
    );
    return this.fromApiSummary(raw);
  }

  async getDetails(projectId: number): Promise<RfpAnalysisDetail[]> {
    const raw = await this.httpClient.get<RfpAnalysisDetailApiResponse[]>(
      `${BASE}/project/${projectId}/details`,
    );
    return raw.map((row) => this.fromApiDetail(row));
  }

  /** GET /project/{projectId}/modules: distinct modules stored on the project's details. */
  async getModules(projectId: number): Promise<string[]> {
    const raw = await this.httpClient.get<string[]>(
      `${BASE}/project/${projectId}/modules`,
    );
    return listRfpAnalysisModules(raw ?? []);
  }

  /** POST /details takes and returns an array. */
  async createDetail(
    input: CreateRfpAnalysisDetailInput,
  ): Promise<RfpAnalysisDetail> {
    const raw = await this.httpClient.post<RfpAnalysisDetailApiResponse[]>(
      `${BASE}/details`,
      { body: [this.toDetailPayload(input)] },
    );
    const created = Array.isArray(raw) ? raw[0] : raw;
    return this.fromApiDetail(created);
  }

  async updateDetail(
    id: number,
    data: UpdateRfpAnalysisDetailInput,
  ): Promise<RfpAnalysisDetail> {
    const raw = await this.httpClient.put<RfpAnalysisDetailApiResponse>(
      `${BASE}/details/${id}`,
      { body: this.toDetailPayload(data) },
    );
    return this.fromApiDetail(raw);
  }

  async getDetailHistory(id: number): Promise<RfpAnalysisDetailHistory[]> {
    return this.httpClient.get<RfpAnalysisDetailHistory[]>(
      `${BASE}/details/${id}/history`,
    );
  }

  /**
   * POST /pms/rfp-analysis/generate/{projectId} (multipart). AI Brain saves the
   * summary and details to PMS as AI / Draft and reports each save outcome.
   */
  async generate(
    params: GenerateRfpAnalysisParams,
  ): Promise<GenerateRfpAnalysisResult> {
    const rfp = params.rfpDocument?.[0];
    if (!rfp) {
      throw new Error("Upload one RFP document.");
    }

    const formData = new FormData();
    formData.append("rfp_document", rfp);
    const mom = params.minutesOfMeeting?.[0];
    if (mom) formData.append("mom_document", mom);
    const message = params.message?.trim();
    if (message) formData.append("message", message);

    const raw = await this.httpClient.post<Partial<RfpGenerationApiResponse>>(
      `${BASE}/generate/${params.projectId}`,
      { body: formData },
    );
    if (!raw?.data) {
      throw new Error("RFP Analysis generation did not complete. Please try again.");
    }

    const detailsSave = raw.data.pms_save;
    if (detailsSave && detailsSave.success === false) {
      throw new Error(
        `RFP Analysis was generated but could not be saved: ${saveErrors(detailsSave)}`,
      );
    }
    const summarySave = raw.data.pms_summary_save;
    return {
      detailsSaved: detailsSave?.saved_count ?? 0,
      summarySaved: summarySave?.success !== false,
      summaryError:
        summarySave?.success === false ? saveErrors(summarySave) : undefined,
    };
  }
}

function saveErrors(save: RfpGenerationPmsSave): string {
  const errors = (save.errors ?? []).map(String).filter(Boolean);
  return errors.length ? errors.join("; ") : "unknown error";
}
